import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import CompensationCard from 'buyside/components/presentation/CompensationIndexPage/CompensationCard/compensationCard';
import CompensationIndexFilters from 'buyside/components/presentation/CompensationIndexPage/compensationIndexFilters';
import IndexPageHeader from 'buyside/components/presentation/IndexPageHeader/indexPageHeader';
import IndexPageListArea from 'buyside/components/presentation/IndexPageListArea/indexPageListArea';
import IndexPaginationNav from 'buyside/components/presentation/IndexPaginationNav/indexPaginationNav';
import Modal from 'buyside/components/presentation/Modal/modal';

import { formatCompensations } from 'buyside/util/formatNumber';
import { validatePageParam } from 'buyside/util/numbers';
import { compensationNewUrl } from 'buyside/util/urlHelper';
import { BREAKPOINTS, useIsMobile } from 'buyside/util/hooks/useIsMobile';
const { indexTypes } = require('buyside/util/constantsFromRails.json');

import { useCompensationIndexListQuery } from 'interfaces/graphql-typings';

import styles from 'buyside/components/presentation/CompensationIndexPage/compensationIndexPageStyles.scss';

const CompensationIndexPage = (): ReactElement => {
  const [searchParams] = useSearchParams();
  validatePageParam(searchParams);

  const [isStatisticsModalOpen, setIsStatisticsModalOpen] = useState(false);

  const { data, loading, error, previousData } = useCompensationIndexListQuery({
    variables: { searchParams: searchParams.toString(), indexType: indexTypes.compensation }
  });

  const indexListResultCount = (data || previousData)?.compensationIndexListResultCount || 0;

  const isMobile = useIsMobile(BREAKPOINTS.LARGE);

  const statistics = useMemo(
    () => (
      <>
        <div>
          Min <div>{formatCompensations(data?.compensationStatistics?.minComp)}</div>
        </div>
        <div>
          Max <div>{formatCompensations(data?.compensationStatistics?.maxComp)}</div>
        </div>
        <div>
          Mean <div>{formatCompensations(data?.compensationStatistics?.mean)}</div>
        </div>
        <div>
          Median <div>{formatCompensations(data?.compensationStatistics?.median)}</div>
        </div>
      </>
    ),
    [data?.compensationStatistics]
  );

  const renderOpenStatisticsModalButton = useCallback(
    () => (
      <Button
        onClick={() => setIsStatisticsModalOpen(true)}
        size={BUTTON_SIZES.TINY}
        text="Compensation Statistics"
      />
    ),
    []
  );

  const renderCheckboxes = useCallback(
    () => (
      <>
        {isMobile ? (
          <Modal
            isOpen={isStatisticsModalOpen}
            onClose={() => setIsStatisticsModalOpen(false)}
            renderOpenModalButton={renderOpenStatisticsModalButton}
            title="Compensation Statistics"
          >
            <div className={styles.modalStatistics}>{statistics}</div>
          </Modal>
        ) : (
          <div className={styles.statisticsContainer}>
            <div className={styles.header}>Compensation Statistics</div>
            <div className={styles.statistics}>{statistics}</div>
          </div>
        )}
      </>
    ),
    [isMobile, isStatisticsModalOpen, renderOpenStatisticsModalButton, statistics]
  );
  const renderFilters = useCallback(() => <CompensationIndexFilters />, []);
  const renderSideButtons = useCallback(
    () => (
      <Button
        size={BUTTON_SIZES.SMALL}
        text="Submit Compensation"
        type={BUTTON_TYPES.MAJOR}
        url={compensationNewUrl()}
      />
    ),
    []
  );

  if (error) throw error;

  return (
    <main className={styles.main}>
      <div className={styles.indexPageContainer}>
        <div className={styles.mainIndexPageArea}>
          <IndexPageHeader
            header="Compensation"
            renderCheckboxes={renderCheckboxes}
            renderFilters={renderFilters}
            renderSideButtons={renderSideButtons}
            resultCount={indexListResultCount}
            showMoreLess={useIsMobile(BREAKPOINTS.LARGER)}
          />
          <IndexPageListArea
            data={(data || previousData)?.compensationIndexList}
            loading={loading}
            currentUser={(data || previousData)?.currentUser}
            card={CompensationCard}
            isMobile={isMobile}
          />
          <IndexPaginationNav
            loading={loading}
            resultCount={indexListResultCount}
            resultLength={data?.compensationIndexList?.edges?.length || 0}
            resultsPerPage={10}
          />
        </div>
      </div>
    </main>
  );
};

export default CompensationIndexPage;
